import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  Alert,
  Skeleton,
  Chip,
  TextField,
  MenuItem,
  TableSortLabel,
  Tooltip,
  LinearProgress,
  Collapse,
  IconButton,
  Stack,
  Divider,
  Link,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  AttachMoney,
  TrendingUp,
  Business,
  Receipt,
  Download,
  EmojiEvents,
  Refresh,
  FilterList,
  InfoOutlined,
  Sync,
  KeyboardArrowDown,
  KeyboardArrowUp,
  OpenInNew,
  Lock,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useAuth } from '../contexts/AuthContext';
import { ConfirmDialog, PageContainer, PageHeader, StatusChip } from '../components/ui';
import { formatDate } from '../utils/productDetailHelpers';
import { integrationApiService } from '../services/integrationApi';
import { useOrgFeatureFlags } from '../hooks/useOrgFeatureFlags';
import { useAppTranslation } from '../i18n/useAppTranslation';
import { meetsReportLevel, ReportLevel } from '../types/billing';
import { useIsMobile } from '../hooks/useIsMobile';
import { tokens } from '../theme/tokens';
import { ReorderSuggestionsSection } from '../components/reorder/ReorderSuggestionsSection';
import { LockedFeature } from '../components/planLock/LockedFeature';
import { usePlanSurface } from '../components/planLock/usePlanSurface';

const mobileLabelSx = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase' as const,
  letterSpacing: '.06em',
  color: tokens.ink.faint,
  display: 'block',
};

interface SellThroughBrand {
  brandId: string;
  brandName: string;
  period: { start: string; end: string };
  unitsSold: number;
  grossRevenue: number;
  totalDiscounts: number;
  netRevenue: number;
  avgSellingPrice: number;
  rebateEligibleUnits: number;
  totalRebateAmount: number;
  dealerCost?: number;
  unitsWithDealerCost?: number;
  estimatedGrossProfit?: number;
  promasterDealerCost?: PromasterCostCoverage;
  topProducts: Array<{
    sku: string;
    name: string;
    unitsSold: number;
    revenue: number;
    dealerCost?: number;
    estimatedGrossProfit?: number;
    promasterDealerCost?: PromasterCostCoverage;
  }>;
}

/** Units whose dealer cost came from Promaster's warehouse feed (flag promaster_dealer_cost). */
interface PromasterCostCoverage {
  units: number;
  asOf: string;
  stale: boolean;
}

interface SellThroughSummary {
  period: { start: string; end: string };
  totalUnitsSold: number;
  totalGrossRevenue: number;
  totalDiscounts: number;
  totalNetRevenue: number;
  totalRebateEligibleUnits: number;
  totalRebateAmount: number;
  /** 'stamped' when the rebate report was unavailable and stored order flags were used. */
  rebateSource?: 'report' | 'stamped';
  totalDealerCost?: number;
  totalEstimatedGrossProfit?: number;
  unitsWithDealerCost?: number;
  promasterDealerCost?: PromasterCostCoverage;
  /** Units sold while the product was announced or in pre-order (flag `preorder_lifecycle`); absent when none. */
  preorderUnits?: number;
  brandCount: number;
  brands: SellThroughBrand[];
  /**
   * The manufacturer rollup of this same summary, present only when the
   * caller passes `GET /by-manufacturer`'s gates (flag manufacturer_reporting,
   * manufacturerReportLevel >= advanced), so the page never runs the order
   * pipeline twice.
   */
  byManufacturer?: ManufacturerSummary;
}

interface TopProduct {
  sku: string;
  name: string;
  brandName: string;
  unitsSold: number;
  revenue: number;
  dealerCost?: number;
  estimatedGrossProfit?: number;
  promasterDealerCost?: PromasterCostCoverage;
}

/** Marks a dealer cost that came (in part) from Promaster, with the date it was reported. */
function PromasterCostTag({ coverage }: { coverage?: PromasterCostCoverage }) {
  const { t } = useAppTranslation();
  if (!coverage || coverage.units <= 0) return null;
  const date = formatDate(coverage.asOf);
  const caption = coverage.stale
    ? t('store.promasterDealerCost.staleCaption', { date })
    : t('store.promasterDealerCost.costCaption', { date });
  return (
    <Tooltip title={coverage.stale ? t('store.promasterDealerCost.staleTooltip', { date }) : caption}>
      <span>
        <StatusChip
          label={t('store.promasterDealerCost.rowChip')}
          tone={coverage.stale ? 'caution' : 'info'}
          size="small"
          sx={{ ml: 0.75, height: 18, '& .MuiChip-label': { px: 0.6, fontSize: 10.5 } }}
        />
      </span>
    </Tooltip>
  );
}

interface ManufacturerRow {
  manufacturer: string;
  manufacturerKey: string;
  brands: Array<{ brandId: string; brandName: string }>;
  unitsSold: number;
  grossRevenue: number;
  totalDiscounts: number;
  netRevenue: number;
  rebateEligibleUnits: number;
  totalRebateAmount: number;
  shareOfNetRevenue: number;
  dealerCost?: number;
  estimatedGrossProfit?: number;
  topProducts: Array<{ sku: string; name: string; brandName: string; unitsSold: number; revenue: number }>;
}

interface ManufacturerSummary {
  manufacturerCount: number;
  totalNetRevenue: number;
  manufacturers: ManufacturerRow[];
}

/** Where sold lines with no catalog brand land (`sellThroughService`). */
const UNMATCHED_BRAND_ID = '_unmatched';

type SortField = 'brandName' | 'unitsSold' | 'grossRevenue' | 'netRevenue' | 'rebateEligibleUnits' | 'totalRebateAmount' | 'dealerCost' | 'estimatedGrossProfit';
type SortDir = 'asc' | 'desc';

const SORT_FIELD_LABELS: Record<SortField, string> = {
  brandName: 'Vendor',
  unitsSold: 'Units Sold',
  grossRevenue: 'Revenue',
  netRevenue: 'Net Revenue',
  rebateEligibleUnits: 'Rebate Eligible Units',
  totalRebateAmount: 'Rebates',
  dealerCost: 'Dealer Cost',
  estimatedGrossProfit: 'Est. Margin',
};

const fmt = (cents: number) => {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const fmtExact = (cents: number) => {
  const dollars = cents / 100;
  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const num = (n: number) => n.toLocaleString();

export const SalesReportPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { impersonatedOrg } = useImpersonation();
  const { isAdmin } = useAuth();
  const { t } = useAppTranslation();
  const { isEnabled: isOrgFlagEnabled } = useOrgFeatureFlags();
  const reportLock = usePlanSurface({ rank: { key: 'reportLevel', minimum: 'advanced' } });
  const manufacturerLock = usePlanSurface({
    rank: { key: 'manufacturerReportLevel', minimum: 'advanced' },
    orgFlag: 'manufacturer_reporting',
  });
  // "Unmatched" sold lines link to the SKU Crosswalk review where that page exists (flag `sku_crosswalk`).
  const matchLinkVisible = isOrgFlagEnabled('sku_crosswalk');
  const lightspeedSalesSyncEnabled = isOrgFlagEnabled('lightspeed_sales_sync');
  // Flag `manufacturer_reporting`: the By manufacturer section below the brand table.
  const manufacturerReportingEnabled = isOrgFlagEnabled('manufacturer_reporting');
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(30, 'day'));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [brandFilter, setBrandFilter] = useState<string>('');
  const [sortField, setSortField] = useState<SortField>('netRevenue');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [exporting, setExporting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMode, setSyncMode] = useState<'incremental' | 'full' | null>(null);
  const [fullResyncConfirmOpen, setFullResyncConfirmOpen] = useState(false);
  const [syncMessage, setSyncMessage] = useState<{ type: 'success' | 'warning' | 'error'; text: string } | null>(null);
  const [expandedBrand, setExpandedBrand] = useState<string | null>(null);

  // Scope cached results to the impersonated org so switching stores doesn't
  // surface another store's data.
  const orgScope = impersonatedOrg?.id ?? 'self';

  const queryParams = useMemo(() => ({
    startDate: startDate.format('YYYY-MM-DD'),
    endDate: endDate.format('YYYY-MM-DD'),
    ...(brandFilter ? { brandId: brandFilter } : {}),
  }), [startDate, endDate, brandFilter]);

  const qs = (params: Record<string, string>) =>
    Object.entries(params).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');

  // GET /store/sell-through/top-products and /export are gated server-side on
  // `reportLevelGate('advanced')` — mirror that gate here so Starter/Pro never
  // fire a request that 403s, and point at the add-on that lifts it instead.
  const { data: subscription, isPending: subscriptionPending } = useQuery<{
    plan?: { limits?: { reportLevel?: ReportLevel; manufacturerReportLevel?: ReportLevel } };
  }>({
    queryKey: ['my-subscription'],
    queryFn: async () => {
      const res = await makeApiCall('/billing/my-subscription', { headers: await getAuthHeaders() });
      // Throw rather than returning {}: a failed call must not read as "no
      // limits", which would show an Intelligence store the locked state and
      // upsell it an add-on its plan already includes. Left unresolved,
      // `hasAdvancedReports` stays undefined and the page behaves as it did
      // before this gate existed.
      if (!res.ok) throw new Error('Failed to load subscription');
      return (await res.json()).data || {};
    },
  });
  // Undefined means the plan is not known: fetch in flight, fetch failed, or a
  // cached `{}` written under this same `['my-subscription']` key by
  // MapPricingDataViewPage, which returns `{}` on `!res.ok` rather than
  // throwing. Reading any of those as `basic` would show a paying Intelligence
  // store the locked state and upsell it a rung its plan already includes —
  // the exact failure this gate exists to prevent. Unknown renders unlocked and
  // lets the request through; the server gate is authoritative, so the worst
  // case is the 403 the page already returned before this gate existed.
  //
  // Limits present but no `reportLevel` IS `basic`, not unknown: the
  // free-preview fallback in routes/billing.ts hand-builds `limits` with only
  // the four count keys and never runs it through `normalizePlanLimits`, so a
  // missing rank is a real payload — and per CLAUDE.md a missing key must
  // never unlock a feature.
  const reportLevel = subscription?.plan?.limits?.reportLevel;
  const hasAdvancedReports =
    subscription?.plan?.limits != null
      ? meetsReportLevel(reportLevel, 'advanced')
      : undefined;
  // Same three-state rule for the Manufacturer Reporting rank
  // (`rankedLevelGate('manufacturerReportLevel', 'advanced')` on
  // GET /store/sell-through/by-manufacturer, which also decides whether the
  // summary carries `byManufacturer`): its own key, never reportLevel.
  const manufacturerReportLevel = subscription?.plan?.limits?.manufacturerReportLevel;
  const hasManufacturerReports =
    subscription?.plan?.limits != null
      ? meetsReportLevel(manufacturerReportLevel, 'advanced')
      : undefined;

  const {
    data: summary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
    error: summaryError,
  } = useQuery<SellThroughSummary>({
    queryKey: ['sell-through-summary', orgScope, queryParams],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/sell-through/summary?${qs(queryParams)}`, { headers });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        const message =
          json?.error?.message || json?.error || json?.message || `Failed to load sales data (HTTP ${res.status})`;
        console.error('[SalesReport] Summary API error:', message);
        throw new Error(message);
      }
      if (!json) {
        throw new Error('API returned invalid response. Is the backend server running?');
      }
      return (json.data || json) as SellThroughSummary;
    },
    retry: false,
    staleTime: 60_000,
  });

  const {
    data: topProducts,
    isLoading: topLoading,
    error: topProductsError,
  } = useQuery<TopProduct[]>({
    queryKey: ['sell-through-top-products', orgScope, queryParams],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/sell-through/top-products?${qs(queryParams)}&limit=10`, { headers });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        const message =
          json?.error?.message || json?.error || json?.message || `Failed to load top products (HTTP ${res.status})`;
        console.error('[SalesReport] Top products API error:', message);
        throw new Error(message);
      }
      return (json?.data || json || []) as TopProduct[];
    },
    // reportLevelGate('advanced') on GET /store/sell-through/top-products.
    // Skip it only while the plan is still loading (so a Starter store does
    // not fire a throwaway 403 on every page load) and when the plan is known
    // to be below Advanced. An unknown plan — a failed subscription call, or a
    // cached `{}` from another page — falls through to the request, matching
    // the export button and the fail-open rule above. `=== true` here would
    // have left an entitled store with no Top Products for the whole session
    // whenever /billing/my-subscription was unavailable.
    enabled: !subscriptionPending && hasAdvancedReports !== false,
    retry: false,
    staleTime: 60_000,
  });

  // By manufacturer: carried on the summary response (`byManufacturer`), not
  // fetched separately, because the server builds it from the summary it has
  // just computed. Absent once the summary has loaded means the server's gate
  // refused it, so the section shows the locked state rather than an empty
  // table; the server is authoritative over the client's plan read.
  const byManufacturer = summary?.byManufacturer;
  const byManufacturerLoading = summaryLoading;
  const byManufacturerError = summaryError;
  const manufacturerLocked = hasManufacturerReports === false || (summary != null && !summary.byManufacturer);

  // Whether an order source is connected. Unknown (loading / failed) keeps the
  // sync buttons as they were; only a known-empty list swaps them for a link.
  const { data: orderSyncIntegrations } = useQuery({
    queryKey: ['sales-report-order-sync', orgScope, lightspeedSalesSyncEnabled],
    queryFn: async () => {
      const list = await integrationApiService.getIntegrations();
      return Array.isArray(list) ? list : [];
    },
    retry: false,
    staleTime: 60_000,
  });
  const orderSyncSetUp =
    orderSyncIntegrations === undefined
      ? undefined
      : orderSyncIntegrations.some(
          (i) =>
            (i.platform === 'shopify' && i.orderSyncEnabled) ||
            (lightspeedSalesSyncEnabled && i.platform === 'lightspeed' && i.isActive),
        );

  const handleExport = async () => {
    // Strictly false, matching the render below: only a plan known to be below
    // Advanced is diverted. `undefined` (loading / unknown) falls through to
    // the export attempt rather than upselling a paying store on a cold cache.
    if (hasAdvancedReports === false) {
      // reportLevelGate('advanced') on GET /store/sell-through/export — send
      // the store to buy the add-on instead of a request that 403s.
      navigate('/app/billing');
      return;
    }
    setExporting(true);
    try {
      const headers = await getAuthHeaders();
      const res = await makeApiCall(`/store/sell-through/export?${qs(queryParams)}`, { headers });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sell-through-${queryParams.startDate}-to-${queryParams.endDate}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // silently fail for now
    } finally {
      setExporting(false);
    }
  };

  const handleSyncOrders = async (fullResync = false) => {
    setSyncing(true);
    setSyncMode(fullResync ? 'full' : 'incremental');
    setSyncMessage(null);
    try {
      const integrations = await integrationApiService.getIntegrations();
      const shopify = integrations.find((i) => i.platform === 'shopify' && i.orderSyncEnabled);
      // Sales sync has no per-integration toggle — any connected Lightspeed
      // integration is eligible once the org flag is on.
      const lightspeed = lightspeedSalesSyncEnabled
        ? integrations.find((i) => i.platform === 'lightspeed' && i.isActive)
        : undefined;
      const target = shopify ?? lightspeed;

      if (!target) {
        setSyncMessage({
          type: 'error',
          text: t(
            lightspeedSalesSyncEnabled
              ? 'store.salesReport.noSyncableIntegrationWithLightspeed'
              : 'store.salesReport.noSyncableIntegration',
          ),
        });
        return;
      }

      const result =
        target.platform === 'shopify'
          ? await integrationApiService.syncShopifyOrders(target.id, { fullResync })
          : await integrationApiService.syncLightspeedSales(target.id, { fullResync });

      // Lightspeed (and Shopify) pullSales/order sync return HTTP 200 with
      // errors[] for auth/credential failures — treat those as a failed sync
      // rather than the zero-order success the raw response would suggest.
      const synced = result.lineItemsSynced ?? 0;
      const processed = result.ordersProcessed ?? 0;
      if (result.errors?.length) {
        if (processed === 0 && synced === 0) {
          // Nothing made it through — a clean failure.
          setSyncMessage({
            type: 'error',
            text: t('store.salesReport.syncFailed', { message: result.errors[0] }),
          });
          return;
        }
        // pullSales returns partway through its sale loop (e.g. a 401 partway
        // through, or the loop's own auth guard) with rows already written —
        // surface what synced and refresh the data instead of hiding it
        // behind an error message the user can't act on until they reload.
        setSyncMessage({
          type: 'warning',
          text: t('store.salesReport.syncPartial', { processed, synced, message: result.errors[0] }),
        });
        queryClient.invalidateQueries({ queryKey: ['sell-through-summary'] });
        queryClient.invalidateQueries({ queryKey: ['sell-through-top-products'] });
        return;
      }

      const baseText = `Synced ${processed} orders, ${synced} line items${fullResync ? ' (full re-sync)' : ''}`;
      setSyncMessage({
        type: 'success',
        text:
          processed === 0 && !fullResync
            ? `${baseText}. No new orders since the last sync — try "Full re-sync" to pull all history.`
            : baseText,
      });
      queryClient.invalidateQueries({ queryKey: ['sell-through-summary'] });
      queryClient.invalidateQueries({ queryKey: ['sell-through-top-products'] });
    } catch (err: any) {
      const errText = typeof err === 'string' ? err : err?.message || 'Sync failed';
      setSyncMessage({ type: 'error', text: errText });
    } finally {
      setSyncing(false);
      setSyncMode(null);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedBrands = useMemo(() => {
    if (!summary?.brands) return [];
    return [...summary.brands].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      return sortDir === 'asc'
        ? ((aVal as number) || 0) - ((bVal as number) || 0)
        : ((bVal as number) || 0) - ((aVal as number) || 0);
    });
  }, [summary?.brands, sortField, sortDir]);

  const hasData = summary && summary.totalUnitsSold > 0;
  const showDealerCost = Boolean(summary?.brands?.some((b) => b.dealerCost != null));
  const brandColSpan = showDealerCost ? 8 : 6;
  const cardSx = { height: '100%' };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <PageContainer>
        <PageHeader title="Sales Report" description="Track your sales by vendor and monitor rebates" />

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <FilterList sx={{ color: 'text.secondary' }} />
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(v) => v && setStartDate(v)}
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(v) => v && setEndDate(v)}
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />
          {summary && summary.brands.length > 1 && (
            <TextField
              select
              size="small"
              label="Brand"
              value={brandFilter}
              onChange={(e) => setBrandFilter(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              <MenuItem value="">All Brands</MenuItem>
              {summary.brands.map((b) => (
                <MenuItem key={b.brandId} value={b.brandId}>{b.brandName}</MenuItem>
              ))}
            </TextField>
          )}
          <Box sx={{ flex: 1 }} />
          {orderSyncSetUp === false ? (
            <Tooltip title={t('store.salesReport.setUpOrderSyncTooltip')}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<Sync />}
                onClick={() => navigate('/app/integrations')}
                sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {t('store.salesReport.setUpOrderSync')}
              </Button>
            </Tooltip>
          ) : (
            <>
              <Tooltip title={lightspeedSalesSyncEnabled ? t('store.salesReport.syncTooltip') : 'Pull new orders from Shopify since the last sync'}>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={syncing && syncMode === 'incremental' ? <CircularProgress size={16} color="inherit" /> : <Sync />}
                    onClick={() => handleSyncOrders(false)}
                    disabled={syncing}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    {syncing && syncMode === 'incremental' ? 'Syncing...' : 'Sync Orders'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={lightspeedSalesSyncEnabled ? t('store.salesReport.fullResyncTooltip') : 'Re-pull the entire order history (use if orders are missing)'}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={syncing && syncMode === 'full' ? <CircularProgress size={16} /> : <Sync />}
                    onClick={() => setFullResyncConfirmOpen(true)}
                    disabled={syncing}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    {syncing && syncMode === 'full' ? 'Re-syncing...' : 'Full re-sync'}
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<Refresh />}
            onClick={() => refetchSummary()}
            disabled={summaryLoading}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Refresh
          </Button>
          <Tooltip title={hasAdvancedReports === false ? t('store.salesReport.exportLockedTooltip') : ''}>
            <span>
              <Button
                variant="outlined"
                size="small"
                startIcon={
                  exporting ? <CircularProgress size={16} /> : hasAdvancedReports === false ? <Lock /> : <Download />
                }
                onClick={handleExport}
                disabled={hasAdvancedReports === false ? false : exporting || !hasData}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {exporting ? 'Exporting...' : 'Export CSV'}
              </Button>
            </span>
          </Tooltip>
        </Paper>

        {syncMessage && (
          <Alert
            severity={syncMessage.type}
            onClose={() => setSyncMessage(null)}
            sx={{ mb: 2, borderRadius: 2 }}
          >
            {syncMessage.text}
          </Alert>
        )}

        {summaryError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            Unable to load sales data. Please try again or contact support if the issue persists.
            {isAdmin && (
              <Box component="details" sx={{ mt: 1, fontSize: '0.85em', opacity: 0.8 }}>
                <summary style={{ cursor: 'pointer' }}>Technical details</summary>
                <code style={{ display: 'block', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {summaryError instanceof Error
                    ? summaryError.message
                    : typeof summaryError === 'string'
                    ? summaryError
                    : (summaryError as any)?.message || 'Unknown error'}
                </code>
              </Box>
            )}
          </Alert>
        )}

        {topProductsError && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            Unable to load top products. Please try again or contact support if the issue persists.
            {isAdmin && (
              <Box component="details" sx={{ mt: 1, fontSize: '0.85em', opacity: 0.8 }}>
                <summary style={{ cursor: 'pointer' }}>Technical details</summary>
                <code style={{ display: 'block', marginTop: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {topProductsError instanceof Error
                    ? topProductsError.message
                    : typeof topProductsError === 'string'
                    ? topProductsError
                    : (topProductsError as any)?.message || 'Unknown error'}
                </code>
              </Box>
            )}
          </Alert>
        )}

        {/* KPI Cards */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(auto-fit, minmax(210px, 1fr))' },
          gap: 3,
          mb: 4,
        }}>
          <Card sx={cardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, backgroundColor: 'rgba(0, 172, 193, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                  <AttachMoney sx={{ fontSize: 22, color: 'info.main' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>Net Revenue</Typography>
                  {summaryLoading ? (
                    <Skeleton width={100} height={32} />
                  ) : (
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {summary ? fmt(summary.totalNetRevenue) : '$0'}
                    </Typography>
                  )}
                </Box>
              </Box>
              {summary && summary.totalDiscounts > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Gross: {fmt(summary.totalGrossRevenue)} &middot; Discounts: {fmt(summary.totalDiscounts)}
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, backgroundColor: 'rgba(76, 175, 80, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                  <TrendingUp sx={{ fontSize: 22, color: 'success.main' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>Units Sold</Typography>
                  {summaryLoading ? (
                    <Skeleton width={60} height={32} />
                  ) : (
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {summary ? num(summary.totalUnitsSold) : '0'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, backgroundColor: 'rgba(76, 175, 80, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                  <Business sx={{ fontSize: 22, color: 'success.main' }} />
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>Brands sold</Typography>
                  {summaryLoading ? (
                    <Skeleton width={40} height={32} />
                  ) : (
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {summary ? summary.brandCount : '0'}
                    </Typography>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={cardSx}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ width: 44, height: 44, borderRadius: 2, backgroundColor: 'rgba(255, 152, 0, 0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 }}>
                  <Receipt sx={{ fontSize: 22, color: 'warning.main' }} />
                </Box>
                <Box>
                  <Typography variant="caption" noWrap sx={{ color: 'text.secondary', fontWeight: 500, display: 'block' }}>Rebate Eligible</Typography>
                  {summaryLoading ? (
                    <Skeleton width={80} height={32} />
                  ) : (
                    <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {summary ? fmt(summary.totalRebateAmount) : '$0'}
                    </Typography>
                  )}
                </Box>
              </Box>
              {summary && summary.totalRebateEligibleUnits > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {num(summary.totalRebateEligibleUnits)} units eligible
                </Typography>
              )}
              {summary?.preorderUnits != null && summary.preorderUnits > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {t('salesReport.preorderUnits', { count: summary.preorderUnits })}
                </Typography>
              )}
              {summary?.rebateSource === 'stamped' && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  Rebate figures from stored order flags; the rebate report was unavailable.
                </Typography>
              )}
            </CardContent>
          </Card>

          {showDealerCost && (
            <Card sx={cardSx}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                  <Box sx={(theme) => ({ width: 44, height: 44, borderRadius: 2, backgroundColor: alpha(theme.palette.success.main, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2 })}>
                    <TrendingUp sx={{ fontSize: 22, color: 'success.main' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" noWrap sx={{ color: 'text.secondary', fontWeight: 500, display: 'block' }}>Est. Gross Profit</Typography>
                    {summaryLoading ? (
                      <Skeleton width={100} height={32} />
                    ) : (
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {summary?.totalEstimatedGrossProfit != null ? fmt(summary.totalEstimatedGrossProfit) : '—'}
                      </Typography>
                    )}
                  </Box>
                </Box>
                {summary?.totalDealerCost != null && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Dealer cost {fmt(summary.totalDealerCost)}
                    {summary.unitsWithDealerCost != null && summary.unitsWithDealerCost < summary.totalUnitsSold
                      ? ` · ${num(summary.unitsWithDealerCost)} of ${num(summary.totalUnitsSold)} units`
                      : ''}
                  </Typography>
                )}
                {summary?.promasterDealerCost && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', mt: 0.5, color: summary.promasterDealerCost.stale ? tokens.signal.caution.ink : 'text.secondary' }}
                  >
                    {t(
                      summary.promasterDealerCost.stale
                        ? 'store.promasterDealerCost.salesReportStaleNote'
                        : 'store.promasterDealerCost.salesReportNote',
                      { units: num(summary.promasterDealerCost.units), date: formatDate(summary.promasterDealerCost.asOf) },
                    )}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Sales by Vendor Table */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                Sales by Vendor
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Revenue breakdown by brand{showDealerCost ? ' · dealer cost stays in Motzu, not on your storefront' : ''}
              </Typography>
            </Box>
          </Box>

          {summaryLoading && <LinearProgress sx={{ mb: 2 }} />}

          {isMobile ? (
            <Box>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <TextField
                  select
                  size="small"
                  label="Sort by"
                  value={sortField}
                  onChange={(e) => handleSort(e.target.value as SortField)}
                  sx={{ minWidth: 160 }}
                >
                  {(Object.keys(SORT_FIELD_LABELS) as SortField[])
                    .filter((f) => f !== 'dealerCost' && f !== 'estimatedGrossProfit' ? true : showDealerCost)
                    .map((f) => (
                      <MenuItem key={f} value={f}>{SORT_FIELD_LABELS[f]}</MenuItem>
                    ))}
                </TextField>
                <IconButton
                  size="small"
                  onClick={() => setSortDir(sortDir === 'asc' ? 'desc' : 'asc')}
                  aria-label={sortDir === 'asc' ? 'Sort ascending' : 'Sort descending'}
                >
                  {sortDir === 'asc' ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                </IconButton>
              </Stack>

              <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <Stack divider={<Divider />}>
                  {sortedBrands.length > 0 ? sortedBrands.map((b) => {
                    const isExpanded = expandedBrand === b.brandId;
                    return (
                      <Box key={b.brandId}>
                        <Box
                          onClick={() => setExpandedBrand(isExpanded ? null : b.brandId)}
                          sx={{ p: 2, cursor: 'pointer' }}
                        >
                          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="subtitle2">{b.brandName}</Typography>
                              {matchLinkVisible && b.brandId === UNMATCHED_BRAND_ID && (
                                <Link
                                  component="button"
                                  type="button"
                                  variant="caption"
                                  underline="always"
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    navigate('/app/sku-overrides?review=sold');
                                  }}
                                  sx={{ display: 'block', textAlign: 'left' }}
                                >
                                  {t('store.salesReport.matchUnmatched')}
                                </Link>
                              )}
                              {b.avgSellingPrice > 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  Avg: {fmtExact(b.avgSellingPrice)}
                                </Typography>
                              )}
                            </Box>
                            <IconButton size="small" sx={{ p: 0.5, flexShrink: 0 }}>
                              {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                            </IconButton>
                          </Stack>
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1.5 }}>
                            <Box>
                              <Typography component="span" sx={mobileLabelSx}>Units Sold</Typography>
                              <Typography variant="body2">{num(b.unitsSold)}</Typography>
                            </Box>
                            <Box>
                              <Typography component="span" sx={mobileLabelSx}>Revenue</Typography>
                              <Typography variant="body2">{fmt(b.grossRevenue)}</Typography>
                              {b.totalDiscounts > 0 && (
                                <Typography variant="caption" sx={{ color: 'error.main', display: 'block' }}>
                                  -{fmt(b.totalDiscounts)}
                                </Typography>
                              )}
                            </Box>
                            <Box>
                              <Typography component="span" sx={mobileLabelSx}>Net Revenue</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(b.netRevenue)}</Typography>
                            </Box>
                            <Box>
                              <Typography component="span" sx={mobileLabelSx}>Rebates</Typography>
                              {b.totalRebateAmount > 0 ? (
                                <Tooltip title={`${num(b.rebateEligibleUnits)} eligible units`}>
                                  <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500, display: 'inline-block' }}>
                                    {fmt(b.totalRebateAmount)}
                                  </Typography>
                                </Tooltip>
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                            </Box>
                            {showDealerCost && (
                              <>
                                <Box>
                                  <Typography component="span" sx={mobileLabelSx}>Dealer Cost</Typography>
                                  <Typography variant="body2">
                                    {b.dealerCost != null ? fmt(b.dealerCost) : '—'}
                                    <PromasterCostTag coverage={b.promasterDealerCost} />
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography component="span" sx={mobileLabelSx}>Est. Margin</Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {b.estimatedGrossProfit != null ? fmt(b.estimatedGrossProfit) : '—'}
                                  </Typography>
                                </Box>
                              </>
                            )}
                          </Box>
                        </Box>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ px: 2, pb: 2, backgroundColor: 'background.default' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, pt: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                Top Products — {b.brandName}
                              </Typography>
                              <Button
                                size="small"
                                variant="text"
                                endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/app/products?brand=${encodeURIComponent(b.brandId)}`);
                                }}
                                sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                              >
                                View all
                              </Button>
                            </Box>
                            {b.topProducts && b.topProducts.length > 0 ? (
                              <Paper variant="outlined">
                                <Stack divider={<Divider />}>
                                  {b.topProducts.map((p) => {
                                    const avg = p.unitsSold > 0 ? p.revenue / p.unitsSold : 0;
                                    return (
                                      <Box key={p.sku || p.name} sx={{ p: 1.5 }}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                          <Box sx={{ minWidth: 0 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>{p.name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{p.sku || 'No SKU'}</Typography>
                                          </Box>
                                          <Tooltip title="Find in Product Browser">
                                            <IconButton
                                              size="small"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/app/products?search=${encodeURIComponent(p.sku || p.name)}`);
                                              }}
                                              sx={{ p: 0.25, flexShrink: 0 }}
                                            >
                                              <OpenInNew sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            </IconButton>
                                          </Tooltip>
                                        </Stack>
                                        <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                          <Typography variant="caption" color="text.secondary">Units {num(p.unitsSold)}</Typography>
                                          <Typography variant="caption" color="text.secondary">Rev {fmt(p.revenue)}</Typography>
                                          <Typography variant="caption" color="text.secondary">Avg {fmtExact(avg)}</Typography>
                                          {showDealerCost && (
                                            <Typography variant="caption" color="text.secondary">
                                              Margin {p.estimatedGrossProfit != null ? fmt(p.estimatedGrossProfit) : '—'}
                                            </Typography>
                                          )}
                                        </Stack>
                                      </Box>
                                    );
                                  })}
                                </Stack>
                              </Paper>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                No product breakdown available
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  }) : (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      {summaryLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={32} />
                          <Typography variant="body2" color="text.secondary">Loading sales data...</Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <TrendingUp sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            No sales data for this period
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Adjust the date range or ensure order sync is enabled in your integration's Sync Behavior tab
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  {sortedBrands.length > 0 && summary && (
                    <Box sx={{ p: 2, backgroundColor: 'background.default' }}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Total</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{num(summary.totalUnitsSold)} units</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" color="text.secondary">Revenue {fmt(summary.totalGrossRevenue)}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(summary.totalNetRevenue)}</Typography>
                      </Stack>
                      {showDealerCost && (
                        <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Dealer cost {summary.totalDealerCost != null ? fmt(summary.totalDealerCost) : '—'}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Margin {summary.totalEstimatedGrossProfit != null ? fmt(summary.totalEstimatedGrossProfit) : '—'}
                          </Typography>
                        </Stack>
                      )}
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Box>
          ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.default' }}>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell>
                    <TableSortLabel active={sortField === 'brandName'} direction={sortField === 'brandName' ? sortDir : 'asc'} onClick={() => handleSort('brandName')}>
                      Vendor
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel active={sortField === 'unitsSold'} direction={sortField === 'unitsSold' ? sortDir : 'desc'} onClick={() => handleSort('unitsSold')}>
                      Units Sold
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel active={sortField === 'grossRevenue'} direction={sortField === 'grossRevenue' ? sortDir : 'desc'} onClick={() => handleSort('grossRevenue')}>
                      Revenue
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel active={sortField === 'totalRebateAmount'} direction={sortField === 'totalRebateAmount' ? sortDir : 'desc'} onClick={() => handleSort('totalRebateAmount')}>
                      Rebates
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel active={sortField === 'netRevenue'} direction={sortField === 'netRevenue' ? sortDir : 'desc'} onClick={() => handleSort('netRevenue')}>
                      Net Revenue
                    </TableSortLabel>
                  </TableCell>
                  {showDealerCost && (
                    <>
                      <TableCell align="right">
                        <TableSortLabel active={sortField === 'dealerCost'} direction={sortField === 'dealerCost' ? sortDir : 'desc'} onClick={() => handleSort('dealerCost')}>
                          Dealer Cost
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel active={sortField === 'estimatedGrossProfit'} direction={sortField === 'estimatedGrossProfit' ? sortDir : 'desc'} onClick={() => handleSort('estimatedGrossProfit')}>
                          Est. Margin
                        </TableSortLabel>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedBrands.length > 0 ? sortedBrands.map((b) => {
                  const isExpanded = expandedBrand === b.brandId;
                  return (
                    <React.Fragment key={b.brandId}>
                      <TableRow
                        hover
                        onClick={() => setExpandedBrand(isExpanded ? null : b.brandId)}
                        sx={{ cursor: 'pointer', '& > td': { borderBottom: isExpanded ? 0 : undefined } }}
                      >
                        <TableCell sx={{ width: 40, pr: 0 }}>
                          <IconButton size="small" sx={{ p: 0.5 }}>
                            {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>{b.brandName}</Typography>
                          {matchLinkVisible && b.brandId === UNMATCHED_BRAND_ID && (
                                <Link
                                  component="button"
                                  type="button"
                                  variant="caption"
                                  underline="always"
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    navigate('/app/sku-overrides?review=sold');
                                  }}
                                  sx={{ display: 'block', textAlign: 'left' }}
                                >
                                  {t('store.salesReport.matchUnmatched')}
                                </Link>
                              )}
                          {b.avgSellingPrice > 0 && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              Avg: {fmtExact(b.avgSellingPrice)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{num(b.unitsSold)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{fmt(b.grossRevenue)}</Typography>
                          {b.totalDiscounts > 0 && (
                            <Typography variant="caption" sx={{ color: 'error.main', display: 'block' }}>
                              -{fmt(b.totalDiscounts)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {b.totalRebateAmount > 0 ? (
                            <Tooltip title={`${num(b.rebateEligibleUnits)} eligible units`}>
                              <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500 }}>
                                {fmt(b.totalRebateAmount)}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(b.netRevenue)}</Typography>
                        </TableCell>
                        {showDealerCost && (
                          <>
                            <TableCell align="right">
                              {b.dealerCost != null ? (
                                <Typography variant="body2">
                                  {fmt(b.dealerCost)}
                                  <PromasterCostTag coverage={b.promasterDealerCost} />
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {b.estimatedGrossProfit != null ? (
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(b.estimatedGrossProfit)}</Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">—</Typography>
                              )}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={brandColSpan} sx={{ py: 0, px: 0, borderBottom: isExpanded ? undefined : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ px: 3, py: 2, backgroundColor: 'background.default' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                  Top Products — {b.brandName}
                                </Typography>
                                <Button
                                  size="small"
                                  variant="text"
                                  endIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/app/products?brand=${encodeURIComponent(b.brandId)}`);
                                  }}
                                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                                >
                                  View all in Product Browser
                                </Button>
                              </Box>
                              {b.topProducts && b.topProducts.length > 0 ? (
                                <Table size="small" sx={{ '& td, & th': { py: 0.75, fontSize: '0.82rem' } }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Product</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Units</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Revenue</TableCell>
                                      {showDealerCost && (
                                        <>
                                          <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Dealer Cost</TableCell>
                                          <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Est. Margin</TableCell>
                                        </>
                                      )}
                                      <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>Avg Price</TableCell>
                                      <TableCell sx={{ width: 40 }} />
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {b.topProducts.map((p) => {
                                      const avg = p.unitsSold > 0 ? p.revenue / p.unitsSold : 0;
                                      return (
                                        <TableRow key={p.sku || p.name} hover>
                                          <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>
                                              {p.name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                              {p.sku || 'No SKU'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align="right">{num(p.unitsSold)}</TableCell>
                                          <TableCell align="right">{fmt(p.revenue)}</TableCell>
                                          {showDealerCost && (
                                            <>
                                              <TableCell align="right">{p.dealerCost != null ? fmt(p.dealerCost) : '—'}<PromasterCostTag coverage={p.promasterDealerCost} /></TableCell>
                                              <TableCell align="right">{p.estimatedGrossProfit != null ? fmt(p.estimatedGrossProfit) : '—'}</TableCell>
                                            </>
                                          )}
                                          <TableCell align="right">{fmtExact(avg)}</TableCell>
                                          <TableCell align="right">
                                            <Tooltip title="Find in Product Browser">
                                              <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  navigate(`/app/products?search=${encodeURIComponent(p.sku || p.name)}`);
                                                }}
                                                sx={{ p: 0.25 }}
                                              >
                                                <OpenInNew sx={{ fontSize: 16, color: 'text.secondary' }} />
                                              </IconButton>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                                  No product breakdown available
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={brandColSpan} sx={{ textAlign: 'center', py: 4 }}>
                      {summaryLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size={32} />
                          <Typography variant="body2" color="text.secondary">Loading sales data...</Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <TrendingUp sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                          <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                            No sales data for this period
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            Adjust the date range or ensure order sync is enabled in your integration's Sync Behavior tab
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                )}
                {sortedBrands.length > 0 && summary && (
                  <TableRow sx={{ backgroundColor: 'background.default' }}>
                    <TableCell />
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>Total</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 600 }}>{num(summary.totalUnitsSold)}</Typography></TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(summary.totalGrossRevenue)}</Typography></TableCell>
                    <TableCell align="right">
                      {summary.totalRebateAmount > 0 ? (
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main' }}>{fmt(summary.totalRebateAmount)}</Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 600 }}>{fmt(summary.totalNetRevenue)}</Typography></TableCell>
                    {showDealerCost && (
                      <>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {summary.totalDealerCost != null ? fmt(summary.totalDealerCost) : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {summary.totalEstimatedGrossProfit != null ? fmt(summary.totalEstimatedGrossProfit) : '—'}
                          </Typography>
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>

        {/* Top Products */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                Top Selling Products
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Best performers by units sold
              </Typography>
            </Box>
            <EmojiEvents sx={{ color: 'warning.main', fontSize: 28 }} />
          </Box>

          {hasAdvancedReports === false ? (
            reportLock.mode === 'locked' ? (
              <LockedFeature variant="inline" planName={reportLock.planName} title={t('store.salesReport.topProductsLockedTitle')} />
            ) : (
            <Alert
              severity="info"
              icon={<Lock fontSize="small" />}
              action={
                <Button size="small" onClick={() => navigate('/app/billing')} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                  {t('store.salesReport.topProductsLockedCta')}
                </Button>
              }
              sx={{ borderRadius: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('store.salesReport.topProductsLockedTitle')}</Typography>
              <Typography variant="body2">{t('store.salesReport.topProductsLockedBody')}</Typography>
            </Alert>
            )
          ) : (
          <>
          {topLoading && <LinearProgress sx={{ mb: 2 }} />}

          {isMobile ? (
            !topProducts || topProducts.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                {topLoading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No product data available for this period
                  </Typography>
                )}
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <Stack divider={<Divider />}>
                  {topProducts.map((p, i) => {
                    const avgPrice = p.unitsSold > 0 ? p.revenue / p.unitsSold : 0;
                    return (
                      <Box key={p.sku || p.name} sx={{ p: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
                          <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: i < 3 ? 'warning.main' : 'text.secondary' }}>
                              {i + 1}
                            </Typography>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{p.sku || 'No SKU'}</Typography>
                            </Box>
                          </Stack>
                          {p.brandName && <Chip label={p.brandName} size="small" variant="outlined" sx={{ flexShrink: 0 }} />}
                        </Stack>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 1.5 }}>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>Units</Typography>
                            <Typography variant="body2">{num(p.unitsSold)}</Typography>
                          </Box>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>Revenue</Typography>
                            <Typography variant="body2">{fmt(p.revenue)}</Typography>
                          </Box>
                          <Box>
                            <Typography component="span" sx={mobileLabelSx}>Avg Price</Typography>
                            <Typography variant="body2">{fmtExact(avgPrice)}</Typography>
                          </Box>
                          {showDealerCost && (
                            <>
                              <Box>
                                <Typography component="span" sx={mobileLabelSx}>Dealer Cost</Typography>
                                <Typography variant="body2">
                                  {p.dealerCost != null ? fmt(p.dealerCost) : '—'}
                                  <PromasterCostTag coverage={p.promasterDealerCost} />
                                </Typography>
                              </Box>
                              <Box>
                                <Typography component="span" sx={mobileLabelSx}>Est. Margin</Typography>
                                <Typography variant="body2">{p.estimatedGrossProfit != null ? fmt(p.estimatedGrossProfit) : '—'}</Typography>
                              </Box>
                            </>
                          )}
                        </Box>
                        <Box sx={{ mt: 1.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                            onClick={() => navigate(`/app/products?search=${encodeURIComponent(p.sku || p.name)}`)}
                            sx={{ textTransform: 'none' }}
                          >
                            Find in Product Browser
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
            )
          ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.default' }}>
                  <TableCell sx={{ width: 40 }}>#</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">Units</TableCell>
                  <TableCell align="right">Revenue</TableCell>
                  {showDealerCost && (
                    <>
                      <TableCell align="right">Dealer Cost</TableCell>
                      <TableCell align="right">Est. Margin</TableCell>
                    </>
                  )}
                  <TableCell align="right">Avg Price</TableCell>
                  <TableCell sx={{ width: 40 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {topProducts && topProducts.length > 0 ? topProducts.map((p, i) => {
                  const avgPrice = p.unitsSold > 0 ? p.revenue / p.unitsSold : 0;
                  return (
                    <TableRow key={p.sku || p.name} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: i < 3 ? 'warning.main' : 'text.secondary' }}>
                          {i + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.name}</Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>{p.sku || 'No SKU'}</Typography>
                      </TableCell>
                      <TableCell>
                        {p.brandName ? (
                          <Chip label={p.brandName} size="small" variant="outlined" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">{num(p.unitsSold)}</TableCell>
                      <TableCell align="right">{fmt(p.revenue)}</TableCell>
                      {showDealerCost && (
                        <>
                          <TableCell align="right">{p.dealerCost != null ? fmt(p.dealerCost) : '—'}<PromasterCostTag coverage={p.promasterDealerCost} /></TableCell>
                          <TableCell align="right">{p.estimatedGrossProfit != null ? fmt(p.estimatedGrossProfit) : '—'}</TableCell>
                        </>
                      )}
                      <TableCell align="right">{fmtExact(avgPrice)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Find in Product Browser">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/app/products?search=${encodeURIComponent(p.sku || p.name)}`)}
                            sx={{ p: 0.25 }}
                          >
                            <OpenInNew sx={{ fontSize: 16, color: 'text.secondary' }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={showDealerCost ? 9 : 7} sx={{ textAlign: 'center', py: 4 }}>
                      {topLoading ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No product data available for this period
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          )}
          </>
          )}
        </Paper>

        {/* By manufacturer (flag manufacturer_reporting; rank manufacturerReportLevel) */}
        {manufacturerReportingEnabled && (
          <Paper sx={{ p: 3, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                  {t('store.salesReport.byManufacturerTitle')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('store.salesReport.byManufacturerSubtitle')}
                </Typography>
              </Box>
              <Business sx={{ color: 'primary.main', fontSize: 28 }} />
            </Box>

            {manufacturerLocked ? (
              manufacturerLock.mode === 'locked' ? (
                <LockedFeature variant="inline" planName={manufacturerLock.planName} title={t('store.salesReport.byManufacturerLockedTitle')} />
              ) : (
              <Alert
                severity="info"
                icon={<Lock fontSize="small" />}
                action={
                  <Button size="small" onClick={() => navigate('/app/billing')} sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}>
                    {t('store.salesReport.byManufacturerLockedCta')}
                  </Button>
                }
                sx={{ borderRadius: 2 }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('store.salesReport.byManufacturerLockedTitle')}</Typography>
                <Typography variant="body2">{t('store.salesReport.byManufacturerLockedBody')}</Typography>
              </Alert>
              )
            ) : byManufacturerError ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>{t('store.salesReport.byManufacturerError')}</Alert>
            ) : (
              <>
                {byManufacturerLoading && <LinearProgress sx={{ mb: 2 }} />}
                {(() => {
                  const rows = byManufacturer?.manufacturers ?? [];
                  const showManufacturerCost = rows.some((m) => m.dealerCost != null);
                  if (rows.length === 0) {
                    return (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        {byManufacturerLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">{t('store.salesReport.byManufacturerEmpty')}</Typography>
                        )}
                      </Box>
                    );
                  }
                  if (isMobile) {
                    return (
                      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                        <Stack divider={<Divider />}>
                          {rows.map((m) => (
                            <Box key={m.manufacturerKey} sx={{ p: 2 }}>
                              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.manufacturer}</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {m.brands.map((b) => b.brandName).join(', ')}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>{fmt(m.netRevenue)}</Typography>
                              </Stack>
                              <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
                                <Box>
                                  <Typography sx={mobileLabelSx}>{t('store.salesReport.byManufacturerColUnits')}</Typography>
                                  <Typography variant="body2">{num(m.unitsSold)}</Typography>
                                </Box>
                                <Box>
                                  <Typography sx={mobileLabelSx}>{t('store.salesReport.byManufacturerColRebates')}</Typography>
                                  <Typography variant="body2">{fmt(m.totalRebateAmount)}</Typography>
                                </Box>
                                <Box>
                                  <Typography sx={mobileLabelSx}>{t('store.salesReport.byManufacturerColShare')}</Typography>
                                  <Typography variant="body2">{Math.round(m.shareOfNetRevenue * 100)}%</Typography>
                                </Box>
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </Paper>
                    );
                  }
                  return (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'background.default' }}>
                            <TableCell>{t('store.salesReport.byManufacturerColManufacturer')}</TableCell>
                            <TableCell>{t('store.salesReport.byManufacturerColBrands')}</TableCell>
                            <TableCell align="right">{t('store.salesReport.byManufacturerColUnits')}</TableCell>
                            <TableCell align="right">{t('store.salesReport.byManufacturerColNetRevenue')}</TableCell>
                            <TableCell align="right">{t('store.salesReport.byManufacturerColRebates')}</TableCell>
                            {showManufacturerCost && (
                              <>
                                <TableCell align="right">{t('store.salesReport.byManufacturerColDealerCost')}</TableCell>
                                <TableCell align="right">{t('store.salesReport.byManufacturerColMargin')}</TableCell>
                              </>
                            )}
                            <TableCell align="right">{t('store.salesReport.byManufacturerColShare')}</TableCell>
                            <TableCell>{t('store.salesReport.byManufacturerColTopProduct')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((m) => (
                            <TableRow key={m.manufacturerKey} hover>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>{m.manufacturer}</Typography>
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                  {m.brands.map((b) => (
                                    <Chip key={b.brandId} label={b.brandName} size="small" variant="outlined" />
                                  ))}
                                </Stack>
                              </TableCell>
                              <TableCell align="right">{num(m.unitsSold)}</TableCell>
                              <TableCell align="right">{fmt(m.netRevenue)}</TableCell>
                              <TableCell align="right">{fmt(m.totalRebateAmount)}</TableCell>
                              {showManufacturerCost && (
                                <>
                                  <TableCell align="right">{m.dealerCost != null ? fmt(m.dealerCost) : '—'}</TableCell>
                                  <TableCell align="right">{m.estimatedGrossProfit != null ? fmt(m.estimatedGrossProfit) : '—'}</TableCell>
                                </>
                              )}
                              <TableCell align="right">{Math.round(m.shareOfNetRevenue * 100)}%</TableCell>
                              <TableCell>
                                {m.topProducts[0] ? (
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 240 }}>{m.topProducts[0].name}</Typography>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                })()}
              </>
            )}
          </Paper>
        )}

        {/* Reorder suggestions (flag `predictive_reorder`): renders nothing with the flag off. */}
        <ReorderSuggestionsSection />

        {/* Help / Info */}
        {!hasData && !summaryLoading && (
          <Alert severity="info" icon={<InfoOutlined />} sx={(theme) => ({ borderRadius: 2, backgroundColor: alpha(theme.palette.info.main, 0.08), border: `1px solid ${alpha(theme.palette.info.main, 0.4)}` })}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>Getting Started with Sales Reports</Typography>
            <Typography variant="body2">
              {lightspeedSalesSyncEnabled
                ? t('store.salesReport.gettingStartedWithLightspeed')
                : t('store.salesReport.gettingStarted')}
            </Typography>
          </Alert>
        )}

        <ConfirmDialog
          open={fullResyncConfirmOpen}
          title="Re-import all order history?"
          message="Full re-sync ignores the last sync point and re-reads your entire order history from your connected store, not just new orders. Orders you already have are matched against existing records, and anything missing is added. Large stores can take several minutes, and your sales and rebate totals may change when it finishes. For routine updates, use Sync Orders instead."
          confirmLabel="Start full re-sync"
          confirmColor="warning"
          onConfirm={() => {
            setFullResyncConfirmOpen(false);
            void handleSyncOrders(true);
          }}
          onClose={() => setFullResyncConfirmOpen(false)}
        />
      </PageContainer>
    </LocalizationProvider>
  );
};

export default SalesReportPage;
